import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { ClientEnvironment } from '@polyai/common/constants/global.constants';

import { projectInit } from 'actions';
import type { AsrCorrection } from 'api/types';

export interface PreviewState {
  clientEnv: ClientEnvironment;
  deploymentId: string | null;
}

type ActiveConfig = {
  activeTranscriptCorrections: AsrCorrection[];
};

export interface VersionComparisonState {
  beforeDeploymentId: string | null;
  afterDeploymentId: string | null;
}

export interface ProjectState {
  activeLanguageCode: string;
  preview: PreviewState;
  activeConfig: ActiveConfig;
  versionComparisonState: VersionComparisonState;
}

const initialState: ProjectState = {
  activeLanguageCode: '',
  preview: {
    clientEnv: ClientEnvironment.SANDBOX,
    deploymentId: null,
  },
  activeConfig: {
    activeTranscriptCorrections: [],
  },
  versionComparisonState: {
    beforeDeploymentId: null,
    afterDeploymentId: null,
  },
};

const jupiterProjectSlice = createSlice({
  name: 'jupiterProjectInfo',
  initialState,
  reducers: {
    setActiveLanguageCode: (state, { payload }: PayloadAction<string>) => {
      state.activeLanguageCode = payload;
    },
    setPreview: (state, { payload }: PayloadAction<PreviewState>) => {
      state.preview.clientEnv = payload.clientEnv;
      state.preview.deploymentId = payload.deploymentId;
    },
    setActiveTranscriptCorrections: (
      state,
      { payload: activeAsrCorrections }: PayloadAction<AsrCorrection[]>,
    ) => {
      state.activeConfig.activeTranscriptCorrections = activeAsrCorrections;
    },
    setVersionComparisonState: (
      state,
      { payload }: PayloadAction<VersionComparisonState>,
    ) => {
      state.versionComparisonState.afterDeploymentId =
        payload.afterDeploymentId;
      state.versionComparisonState.beforeDeploymentId =
        payload.beforeDeploymentId;
    },
  },
  extraReducers: builder => {
    builder.addCase(projectInit, () => {
      return { ...initialState };
    });
  },
});

export const {
  setActiveLanguageCode,
  setPreview,
  setActiveTranscriptCorrections,
  setVersionComparisonState,
} = jupiterProjectSlice.actions;

export default jupiterProjectSlice.reducer;
